import { cn } from '@/lib/utils';

interface CustomerLabelProps {
  /**
   * The customer's name as the source ERP renders it — the `erpDisplayName`
   * field on the payload. Null when the row has no customer, or when the
   * customer's ERP has no distinct display form.
   */
  erpDisplayName: string | null | undefined;
  /**
   * Raw name from the caller's own payload, used when there is no
   * `erpDisplayName`.
   */
  fallback?: string | null;
  /** Rendered when neither a display name nor a fallback is available. */
  placeholder?: string;
  className?: string;
}

/**
 * Renders a customer's name the way the source ERP names them — Famous-synced
 * customers show as `Acme Foods (ACM)`.
 *
 * Use this anywhere a customer name is displayed instead of printing the
 * `customerName` field directly. The name is computed server-side by
 * `getCustomerErpDisplayName` and carried on the payload as `erpDisplayName`,
 * so surfaces stay consistent without each one formatting its own, and the raw
 * `customerName` stays untouched for sorting, filtering, and export.
 */
export function CustomerLabel({
  erpDisplayName,
  fallback,
  placeholder = '—',
  className,
}: CustomerLabelProps) {
  // `||` rather than `??` so a blank name from either source falls through to
  // the next one instead of rendering an empty cell.
  const label = erpDisplayName || fallback || placeholder;

  return <span className={cn('text-sm', className)}>{label}</span>;
}
